/**
 * The **HEADER** project picker for the deal / quote / invoice / deliverable
 * forms, and the read-only render of the cities that header project derives.
 *
 * ## ⚠️ The header project is no longer the authority for cities. Read this first
 *
 * Migration 067 moved the project onto the **line item**. A quote, invoice or
 * deal line now carries its own `project` (written as `projectId`) and its own
 * set of `cities` (written as `cityIds`), and those are what print on the
 * customer's document.
 *
 * The reason is worth keeping, because it explains why the old arrangement
 * looked correct right up to the moment it wasn't: **a project names exactly
 * ONE product, and a document sells many.** So a single project on the header
 * could never describe more than one line, and two real defects followed from
 * pretending it could — a line's city picker was filtered against a project for
 * a *different product*, and Xero tracking derived from the header project was
 * stamped on every line, so a quote spanning two regions reported into one
 * bucket while both the quote and the Xero document looked completely right.
 *
 * **The header `projectId` STAYS.** It was not removed and nothing this app
 * already sends started failing. Its job is now narrower and precisely two
 * things:
 *
 * | | header project (this file) | line project (`use-line-project.ts`) |
 * | --- | --- | --- |
 * | what it is for | the **default a new line starts from**, and the **fallback for a document with no lines yet** | the **authority** for that line's cities and its Xero tracking |
 * | its `cities` | derived, read-only, a document-level default | stored on the line, and what the customer reads |
 *
 * So: **`citiesOfProject()` below is a DEFAULT and a summary, never a line's
 * option list.** A line editor must resolve its own project (product → region →
 * cycle → intake, via `GET /projects?productId=…`) and take the options from
 * *that* project's `cities`. Populating a line's multiselect from the header —
 * or worse, from the `/cities` master — offers cities the server will refuse,
 * and refuses cities the line is entitled to. That resolution deliberately
 * lives in a separate hook; this file has no business knowing about lines.
 *
 * ## Why a shared module, before there are four copies of it
 *
 * The company stage shipped as three independent copies in one afternoon and
 * two of them disagreed about how to handle a retired value. Four consumers
 * need this one. Writing it once is cheaper than reconciling it later, and the
 * reconciliation is what actually costs.
 *
 * ## The picker filters to active; the server does NOT
 *
 * `projectId` on a deal, quote, invoice or deliverable is resolved **by id
 * only** — the header write checks existence and never `isActive`. (A *line's*
 * `projectId` is checked for both, which is a real asymmetry rather than an
 * oversight; do not carry an assumption either way across.) So a retired
 * project can still sit on a header, and keeping retired ones out of the
 * dropdown is entirely this app's job.
 *
 * That makes the stored-value problem real rather than theoretical: filter the
 * fetch with `?isActive=true` and a record already on a retired project would
 * render blank, then be silently reassigned by the next save. So the stored
 * value is injected back, labelled — the same shape as `withStoredOption()`
 * for the masters, and for the same reason.
 *
 * ## Paging is the trap here, not activity
 *
 * `/projects` defaults to `perPage: 20` and caps at **100** — lower than the
 * masters, which go to 200. There is no "give me all" and no reason to assume a
 * business will stay under a hundred saleable runs. So this reports `hasMore`
 * rather than pretending the first page is the whole list, and a caller must
 * say so rather than showing a dropdown that quietly omits things.
 *
 * ## There is no country helper here, and adding one would undo a decision
 *
 * A project associates **cities and only cities**. `f_project_countries` was
 * dropped in migration 059 and `?countryId=` on `/projects` is now a 422 naming
 * the key. A city carries its own country, so "which countries does this cover"
 * survives through `city.country`; the reverse does not — a project cannot name
 * a country without naming a city in it. The owner was shown that cost and took
 * it. Deduplicating `cities[].country` is *not* the old set under a new name: it
 * silently drops every city with no country recorded, and it never contained a
 * country covered without being visited. Raise it; do not rebuild it.
 */

import { useEffect, useState } from 'react';
import { api } from './api';
import type { MasterRef, Project, ProjectCity } from './types';


/**
 * The two fields a city label actually reads.
 *
 * ⚠️ **Deliberately structural, because there are TWO city types and they must
 * not be merged.** `ProjectCity` — what a project, and therefore a deal, quote,
 * invoice or deliverable *header*, carries — is `{id, name, country}` with **no
 * `sortOrder`**. `LineCity` — what a quote, invoice or deal *line* carries — has
 * all three plus a `sortOrder` that is the line's own curated display order.
 *
 * Naming this shape lets one label helper serve both without either type
 * pretending to be the other. What it must NOT become is licence to write one
 * *renderer* over both: a header list is derived through the record's project
 * and has no per-record order to honour, while a line list is stored and does —
 * the server has already sorted it and re-sorting it changes what the customer
 * reads. A single merged "City" type would make `sortOrder` optional, and an
 * optional number is exactly the thing that gets read as `?? 0` and silently
 * collapses a curated order into insertion order.
 */
export type CityNameParts = Pick<ProjectCity, 'name' | 'country'>;

/**
 * A city always renders WITH its country. City names repeat across countries —
 * there is more than one Cambridge — so a bare name is ambiguous in exactly the
 * situation this list exists to resolve. `country` is nullable only because the
 * join is `LEFT`; a city missing one renders as itself.
 *
 * ⚠️ **It renders the name it was GIVEN, and that is the whole point.** Reads
 * never filter on `isActive`, so a city on an old project or an already-sent
 * document can legitimately name a row that is absent from any `?isActive=true`
 * picker list. Looking the name up in a cached active list and blanking it when
 * it is missing is how a historical document quietly loses its geography — so
 * this takes the name as data and never resolves it against anything.
 */
export function cityLabel(city: CityNameParts): string {
  return city.country ? `${city.name}, ${city.country.name}` : city.name;
}

export interface ProjectOption {
  value: string;
  label: string;
  /**
   * The project's own cities, so the header picker can show what choosing it
   * implies for a document with no lines yet.
   *
   * **Not a line's option list.** A line's cities come from the line's own
   * project — see the header comment. Rendering these beside a line editor
   * would offer exactly the wrong set on any line selling a different product.
   */
  cities: ProjectCity[];
}

export interface ProjectOptionsState {
  projects: Project[];
  loading: boolean;
  failed: boolean;
  /**
   * True when the server holds more projects than one page.
   *
   * Surfaced rather than swallowed: a picker silently missing its 101st option
   * is indistinguishable from that project not existing, and the person who
   * cannot find it has no way to tell which.
   */
  hasMore: boolean;
}

/** Reads active projects once per mount. */
export function useProjectOptions(enabled = true): ProjectOptionsState {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [failed, setFailed] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    /*
     * 🔴 EVERY PAGE, not the first (2026-10-05). *SUPERSEDED: one page of
     * `perPage: 100`.* With ~120 active projects the tail of the alphabet
     * ("Virtual Masterclasses …") never reached any picker built on this hook —
     * the projects-of-interest editor on /companies, /companies/:id and the UP
     * dashboard among them — and the owner reported it as the projects not
     * existing. `listAllProjects` pages to completion and throws rather than
     * resolving short, so `hasMore` can no longer be true; it stays on the
     * state so existing callers' footnote branches compile unchanged.
     */
    api
      .listAllProjects(controller.signal, { isActive: true, sort: 'name', order: 'asc' })
      .then((all) => {
        if (controller.signal.aborted) return;
        setProjects(all);
        setHasMore(false);
        setFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setProjects([]);
        setHasMore(false);
        setFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [enabled]);

  return { projects, loading: enabled ? loading : false, failed, hasMore };
}

/** A blank name would render an unselectable-looking option; the id still identifies it. */
function labelFor(id: number, name: string): string {
  const trimmed = name.trim();
  return trimmed === '' ? `#${id}` : trimmed;
}

/**
 * What one header picker should offer: the active projects, plus `stored` when
 * it is not among them.
 *
 * `stored` is the `{id, name}` the record already carries. It is injected
 * because the server will happily keep a retired project on a record header —
 * it checks existence only — so the only way it disappears is if this app drops
 * it, and dropping it is how a record gets quietly reassigned by the next save.
 *
 * An empty `projects` gets **no** retired label: empty means "still loading" or
 * "failed", never "every project was retired".
 */
export function projectOptions(
  projects: Project[],
  stored: MasterRef | null,
): ProjectOption[] {
  const options = projects.map((project) => ({
    value: String(project.id),
    label: labelFor(project.id, project.name),
    cities: project.cities,
  }));

  if (!stored) return options;
  if (options.some((option) => option.value === String(stored.id))) return options;

  return [
    {
      value: String(stored.id),
      label:
        projects.length > 0
          ? `${labelFor(stored.id, stored.name)} (no longer offered)`
          : labelFor(stored.id, stored.name),
      /*
       * Deliberately empty rather than guessed. The cities of a project not in
       * the list are unknown here, and showing none is honest where showing
       * the previous project's would be a lie.
       */
      cities: [],
    },
    ...options,
  ];
}

/**
 * The cities the chosen **header** project implies, for the four consumer forms
 * to render read-only.
 *
 * **Nothing writes these.** No city is stored on the deal, quote, invoice or
 * deliverable record itself — the set is derived through the header project on
 * every request, so it is context for the person filling the form, never a
 * value to submit, and a UI must not offer to edit it here. (A *line* is the
 * opposite: its cities are stored, written as `cityIds`, and edited in the line
 * editor. Different set, different hook, different type — `LineCity`, which
 * carries a `sortOrder` these do not.)
 *
 * ⚠️ **This is a default and a summary, not a line's option list.** Since the
 * project moved onto the line, "everywhere the header project runs" and "where
 * this line runs" are frequently different sets. Feeding this into a line's city
 * multiselect is the exact defect migration 067 was made to fix.
 *
 * Because these are derived rather than snapshotted, they also drift: drop a
 * city from a project and a deliverable raised last March stops reporting it.
 * That is a known limit of the header set and another reason a line stores its
 * own.
 *
 * Each city carries its own country, which is how "which countries does this
 * cover" survives a project associating cities instead. The reverse does not
 * survive, and is not to be rebuilt — see the header comment.
 */
export function citiesOfProject(
  projects: Project[],
  projectId: number | null,
): ProjectCity[] {
  if (projectId === null) return [];
  return projects.find((project) => project.id === projectId)?.cities ?? [];
}
